import axiosInstance from 'api/axiosInstance';
import type { UpdateYearlyPrepaymentsBody } from 'api/v1/taxes';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { UserOnboarding } from 'components/pages/Taxes/OnboardingV2/types';
import dayjs from 'dayjs';
import appI18nInstance from 'i18n';
import get from 'lodash/get';
import type { IExpenseV2 } from 'types/expenses.types';
import type { Period, VATReturnFrequency } from 'types/global.types';
import {
  TaxTypeEnum,
  type DocumentFetchType,
  type ITax,
  type StatementMap,
  type Submission,
  type TaxCheckEnum,
  type TaxExpensesReasons,
  type TaxRevenuesReasons,
} from 'types/taxes.types';

import { AvailableCountries, type UserCountry } from 'types/users.types';
import { CURRENT_YEAR } from 'utils/constants';
import getPeriodVATParam from 'utils/taxes/getVATPeriodParam';

type GetTaxesParams = {
  perPage: number;
  page: number;
  statuses: string;
  types: string;
  periods: string;
};

type GetTaxExpensesParams = {
  taxId: string;
  perPage?: number;
  page?: number;
  type?: DocumentFetchType;
  reason?: TaxExpensesReasons;
};

type GetTaxRevenuesParams = {
  taxId: string;
  perPage?: number;
  page?: number;
  type?: DocumentFetchType;
  reason?: TaxRevenuesReasons;
};

export const backendTaxTypeFromFrontendTaxType = {
  [AvailableCountries.belgium]: {
    [TaxTypeEnum.clientListing]: 'client-listing',
    [TaxTypeEnum.incomeTaxPayment]: 'income-tax-payment',
    [TaxTypeEnum.incomeTaxPrepayment]: 'income-tax-prepayment',
    [TaxTypeEnum.incomeTaxSubmission]: 'income-tax-submission',
    [TaxTypeEnum.intracomListing]: 'intracom-listing',
    [TaxTypeEnum.scExonerated]: 'social-contribution-exonerated',
    [TaxTypeEnum.scSettings]: 'social-contribution-settings',
    [TaxTypeEnum.scSubject]: 'social-contribution-subject',
    [TaxTypeEnum.vatFranchisee]: 'vat-franchisee',
    [TaxTypeEnum.vatSubmission]: 'vat',
    [TaxTypeEnum.vatPayment]: 'vat',
    [TaxTypeEnum.specialVATSubmission]: 'special-vat',
    [TaxTypeEnum.specialVATPayment]: 'special-vat',
    [TaxTypeEnum.companyTaxPrepayment]: 'company-tax-prepayment',
    [TaxTypeEnum.companyTaxPayment]: 'company-tax-payment',
  },
  [AvailableCountries.germany]: {
    [TaxTypeEnum.vatSubmission]: 'ustva',
    [TaxTypeEnum.vatPayment]: 'ustva',
    [TaxTypeEnum.vatFranchisee]: 'vat-franchisee',
    [TaxTypeEnum.usteSubmission]: 'uste-submission',
    [TaxTypeEnum.ustePayment]: 'uste-payment',
    [TaxTypeEnum.intracomListing]: 'zm',
    [TaxTypeEnum.incomeTaxPayment]: 'est-payment',
    [TaxTypeEnum.incomeTaxPrepayment]: 'est-prepayment',
    [TaxTypeEnum.incomeTaxSubmission]: 'est-submission',
    [TaxTypeEnum.tradeTaxPrepayment]: 'gewst-prepayment',
    [TaxTypeEnum.tradeTaxSubmission]: 'gewst-submission',
    [TaxTypeEnum.EUR]: 'eur',
  },
};

export const getTaxBySlug = (slug: string): Promise<ITax> => {
  return axiosInstance
    .get(`/v2/taxes/resource/${slug}`, {
      ignoreToast: true,
    } as AxiosRequestConfig)
    .then(({ data }) => data);
};

export const getBackendReturnFrequencyFromTaxItemPeriod = (period?: Period) => {
  if (period) {
    if (period.month) {
      return period.month < 10 ? '0' : '';
    }
    if (period.quarter) {
      return '4';
    }
  }
  return '';
};

export const getTaxBySlugV2 = (
  userCountry: UserCountry,
  taxPeriod: Period,
  taxType: TaxTypeEnum,
): Promise<ITax> => {
  const isYearlyReturn = !taxPeriod?.month && !taxPeriod?.quarter;
  const type = (backendTaxTypeFromFrontendTaxType[userCountry] as any)[taxType];
  const formatterdPeriod =
    isYearlyReturn || !taxPeriod
      ? ''
      : `-${getBackendReturnFrequencyFromTaxItemPeriod(taxPeriod)}${
          taxPeriod.month || taxPeriod.quarter || taxPeriod.year
        }`;
  const formattedDate = !taxPeriod
    ? ''
    : `/${taxPeriod.year}${formatterdPeriod}`;

  return axiosInstance
    .get(`/v2/taxes/resource/${userCountry}/${type}${formattedDate}`, {
      ignoreToast: true,
    } as AxiosRequestConfig)
    .then(({ data }) => data);
};

export const getTaxesByYear = (year: number): Promise<any> => {
  return axiosInstance.get(`/v2/taxes/${year}?expand=document`);
};

export const getTaxes = (params: Partial<GetTaxesParams>): Promise<ITax[]> => {
  return axiosInstance.get(`/v2/taxes`, { params });
};

export const getClosedVATPeriods = (
  VATFreq: VATReturnFrequency,
  country: AvailableCountries,
): Promise<Period[]> =>
  axiosInstance
    .get(
      `/v2/taxes?types=${
        country === AvailableCountries.germany && VATFreq === 'yearly'
          ? 'uste_submission'
          : 'vat_submission,uste_submission,vat_payment,intracom_listing'
      }&statuses=done&perPage=${500}`,
    )
    .then(({ data }) => data.map(({ period }: any) => period));

export const getTaxSubmissions = (taxId: string): Promise<Submission[]> => {
  return axiosInstance
    .get(`v2/taxes/${taxId}/submissions`, {
      ignoreToast: true,
    } as AxiosRequestConfig<Promise<Submission[]> & { ignoreToast: boolean }>)
    .then(({ data }) => data);
};

export async function getTaxExpenses({
  taxId,
  perPage = 500,
  page = 1,
  type,
  reason,
}: GetTaxExpensesParams): Promise<AxiosResponse<{ data: IExpenseV2[] }>> {
  return axiosInstance.get(`/v2/taxes/${taxId}/documents/expenses`, {
    params: {
      expense_version: 3,
      lang: appI18nInstance.language,
      perPage,
      page,
      type,
      reason,
    },
  });
}

export type TaxesHeaderCalcs = {
  currentTaxRate: number;
  currentCorporateTaxRate: number;
  currentYearDaysBetweenFirstLastRevenue: number;
  usedFlatFee: number;
  projectedTaxRate: number;
  projectedIncomeTax: number;
  projectedTaxableIncome: number;
  incomeTaxToDate: number;
  taxableIncome: number;
  revenues: number;
  deductibleCosts: number;
  currentYearPrepayments: number;
};

export const getTaxesHeaderCalcs = (
  year?: number,
): Promise<TaxesHeaderCalcs> => {
  return axiosInstance.get('/v2/calculations/taxes', {
    params: { year: year || dayjs().year() },
  });
};

type ProfitProjectionSettings = {
  currentYearDaysBetweenFirstLastRevenue: number;
  currentYearProjectedTaxableIncome: number;
  currentYearTaxableIncome: number;
  manualEstimation: number | null;
  personaTaxableIncome: number;
  previousYearTaxableIncome: number;
};

export const getProfitSettings = (): Promise<ProfitProjectionSettings> => {
  return axiosInstance.get('v2/users/settings/profit');
};

export async function getTaxRevenues({
  taxId,
  perPage = 500,
  page = 1,
  type,
  reason,
}: GetTaxRevenuesParams): Promise<any> {
  return axiosInstance.get(`/v2/taxes/${taxId}/documents/revenues`, {
    params: {
      perPage,
      page,
      type,
      reason,
    },
  });
}

type Params = {
  year: number;
  period: Period;
  path: string;
};

export const getVATStatementBreakdown = async ({
  year,
  period,
  path,
}: Params): Promise<{ statement: StatementMap }> => {
  const periodParam = getPeriodVATParam(period);
  return axiosInstance.get(`v2/taxes/${path}/${year}`, {
    params: {
      period: periodParam,
      enableBreakdown: true,
    },
  });
};

export const getEURStatementBreakdown = async ({
  year,
}: {
  year: number;
}): Promise<{ statement: StatementMap }> => {
  return axiosInstance
    .get(`v2/taxes/de/eur/${year}`, {
      params: { enableBreakdown: true },
    })
    .then((data) => ({
      statement: {
        ...get(data, 'statement.EUR'),
        ...get(data, 'statement.AVEUR'),
      },
    }));
};

export const getSubmissionDetails = (
  taxId: string,
  submissionId: string,
  showBreakdown: boolean,
): Promise<Submission> => {
  return axiosInstance
    .get(`v2/taxes/${taxId}/submissions/${submissionId}`, {
      params: { showBreakdown },
    })
    .then(({ data }) => data);
};

export const getHebesatz = (params: {
  city: string;
  state: string;
  year: number;
}): Promise<{ hebesatz: { value: number; isDefault: boolean } }> => {
  return axiosInstance.get('/v2/taxes/utils/hebesatz', {
    params,
    ignoreToast: true,
  } as AxiosRequestConfig<{
    ignoreToast: string;
    city: string;
    state: string;
  }>);
};

export const getTaxCheck = (
  checkId: TaxCheckEnum,
  tax: ITax,
  page?: number,
): any =>
  axiosInstance.get(`v2/taxes/${tax?._id}/checks/${checkId}`, {
    params: {
      expense_version: 3,
      lang: appI18nInstance.language,
      page,
      perPage: 25,
      status: get(tax, 'guarantee.taxChecks.failed', []).includes(checkId)
        ? 'failed'
        : 'reviewed',
    },
  });

export const reviewTaxCheck = (checkId: TaxCheckEnum, taxId: string): any =>
  axiosInstance.post(`v2/taxes/${taxId}/checks/${checkId}/review`);

export const undoReviewTaxCheck = (checkId: TaxCheckEnum, taxId: string): any =>
  axiosInstance.post(`v2/taxes/${taxId}/checks/${checkId}/undo-review`);

export const updateTaxesOnboarding = (
  values: UserOnboarding,
): Promise<UserOnboarding> => {
  return axiosInstance.post('/v2/taxes/onboarding', values);
};

export const getTaxesOnboarding = (): Promise<UserOnboarding> => {
  return axiosInstance.get('/v2/taxes/onboarding', {
    params: { year: CURRENT_YEAR },
  });
};

export const updateIncomeTaxQuarterlyPrepaymentSetup = (
  params: UpdateYearlyPrepaymentsBody,
) => axiosInstance.post('/v2/taxes/income-tax/yearly-prepayments', params);

export const updateTradeTaxQuarterlyPrepaymentSetup = (
  params: UpdateYearlyPrepaymentsBody,
) => axiosInstance.post('/v2/taxes/trade-tax/yearly-prepayments', params);
